import { useState, useEffect } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Badge } from './badge';
import { Textarea } from './textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import {
    Eye, EyeOff, Edit, Trash2, AlertTriangle, CheckCircle2, Phone, Mail,
    Calendar, DollarSign, User, FileText, RefreshCw, Tag, MapPin,
    Clock, MessageSquare, CalendarDays, UserPlus, UsersRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { createLeadFollowUp, updateLead, updateEmployee, updateAgent } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

// ─── Shared info row ────────────────────────────────────────────────────────
const InfoRow = ({ icon: Icon, label, value, valueClass = '' }) => (
    <div className="flex items-start gap-3 py-2.5 border-b last:border-0">
        <div className="mt-0.5 p-1.5 rounded-md bg-muted">
            <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
            <p className={`text-sm font-semibold mt-0.5 truncate ${valueClass}`}>{value || '—'}</p>
        </div>
    </div>
);

// ─── Status badge ───────────────────────────────────────────────────────────
const statusPalette = {
    New: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    Contacted: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    Converted: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    Pending: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    Rejected: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    Active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    Inactive: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
    'On Leave': 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    'Under Processing': 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    'Resubmission Required': 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
    Approved: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    Disbursed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    Hold: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    Done: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    Completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    Scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    Overdue: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

export const StatusBadge = ({ status }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusPalette[status] || 'bg-gray-100 text-gray-700'}`}>
        {status}
    </span>
);

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

// ═══════════════════════════════════════════════════════════════════════════
// LEAD MODALS
// ═══════════════════════════════════════════════════════════════════════════

export const ViewLeadModal = ({ lead, open, onClose }) => {
    if (!lead) return null;
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[520px]">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                            <Eye className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <DialogTitle className="text-lg">Lead Details</DialogTitle>
                            <DialogDescription className="text-xs">{lead.id}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 mb-2">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
                        {lead.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                        <p className="font-bold text-base">{lead.name}</p>
                        <StatusBadge status={lead.status} />
                    </div>
                </div>

                <div className="space-y-0 rounded-xl border overflow-hidden">
                    <InfoRow icon={Phone} label="Phone" value={lead.phone} />
                    <InfoRow icon={Mail} label="Email" value={lead.email} />
                    <InfoRow icon={FileText} label="Loan Type" value={lead.loanType} />
                    <InfoRow icon={DollarSign} label="Loan Amount" value={fmt(lead.amount)} valueClass="text-emerald-600" />
                    <InfoRow icon={Tag} label="Priority" value={lead.priority} />
                    <InfoRow icon={MapPin} label="Source" value={lead.source} />
                    <InfoRow icon={Calendar} label="Follow-up Date" value={lead.followUpDate || 'Not set'} />
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// Map UI status labels to backend enum values
const displayStatusToBackend = (s) => {
    const map = { New: 'new', Contacted: 'follow_up', Converted: 'converted', Rejected: 'rejected', Pending: 'new' };
    return map[s] || 'new';
};

const normalizeLoanTypeToBackend = (value) => {
    const raw = String(value || '').trim();
    const normalized = raw.toLowerCase().replace(/\s+/g, '_');
    const map = {
        home: 'home',
        home_loan: 'home',
        personal: 'personal',
        personal_loan: 'personal',
        business: 'business',
        business_loan: 'business',
        car: 'car',
        car_loan: 'car',
        education: 'education',
        education_loan: 'education',
        other: 'other',
    };
    return map[normalized] || 'home';
};

export const EditLeadModal = ({ lead, open, onClose, token }) => {
    const [form, setForm] = useState(lead || {});
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (lead) {
            setForm({
                ...lead,
                loanType: normalizeLoanTypeToBackend(lead.loanType),
                source: String(lead.source || 'admin').toLowerCase(),
                priority: lead.priority || 'Medium',
                status: lead.status || 'Pending',
            });
        }
    }, [lead]);
    if (!lead) return null;

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (token) {
                const payload = {
                    customer_name: form.name?.trim(),
                    phone: form.phone?.trim(),
                    email: form.email?.trim() || null,
                    loan_type: normalizeLoanTypeToBackend(form.loanType),
                    loan_amount: Number(form.amount) || 0,
                    source: String(form.source || 'admin').trim().toLowerCase() || 'admin',
                    priority: (form.priority || 'Medium').trim() || 'Medium',
                    status: displayStatusToBackend(form.status),
                };
                await updateLead(token, lead.id, payload);
            }
            toast.success('Lead updated successfully!');
            onClose();
        } catch (err) {
            toast.error(err?.message || 'Failed to update lead');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[540px]">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                            <Edit className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                            <DialogTitle>Edit Lead</DialogTitle>
                            <DialogDescription>{lead.id} — {lead.name}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <form onSubmit={handleSave} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>Full Name</Label>
                            <Input value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Phone</Label>
                            <Input value={form.phone || ''} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} required />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Email</Label>
                        <Input type="email" value={form.email || ''} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>Loan Type</Label>
                            <Select value={form.loanType || 'home'} onValueChange={v => setForm(p => ({ ...p, loanType: v }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {[
                                        { value: 'home', label: 'Home Loan' },
                                        { value: 'personal', label: 'Personal Loan' },
                                        { value: 'business', label: 'Business Loan' },
                                        { value: 'car', label: 'Car Loan' },
                                        { value: 'education', label: 'Education Loan' },
                                        { value: 'other', label: 'Other' },
                                    ].map(({ value, label }) => (
                                        <SelectItem key={value} value={value}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Loan Amount (₹)</Label>
                            <Input type="number" value={form.amount || ''} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>Status</Label>
                            <Select value={form.status || 'Pending'} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {['Contacted', 'Converted', 'Pending', 'Rejected'].map(s => (
                                        <SelectItem key={s} value={s}>{s}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Priority</Label>
                            <Select value={form.priority || 'Medium'} onValueChange={v => setForm(p => ({ ...p, priority: v }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="High">High</SelectItem>
                                    <SelectItem value="Medium">Medium</SelectItem>
                                    <SelectItem value="Low">Low</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>Source</Label>
                            <Select value={form.source || 'admin'} onValueChange={v => setForm(p => ({ ...p, source: v }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {['admin', 'website', 'referral', 'agent', 'walkin'].map(s => (
                                        <SelectItem key={s} value={s}>{s === 'admin' ? 'Admin' : s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white" disabled={saving}>
                            {saving ? <><RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> Saving...</> : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export const UpdateLeadStatusModal = ({ lead, open, onClose, token }) => {
    const [status, setStatus] = useState(lead?.status || '');
    const [saving, setSaving] = useState(false);
    if (!lead) return null;

    const handleSave = async () => {
        setSaving(true);
        try {
            if (token) {
                await updateLead(token, lead.id, { status: displayStatusToBackend(status) });
            }
            toast.success(`Lead status updated to "${status}"`);
            onClose();
        } catch (err) {
            toast.error(err?.message || 'Failed to update status');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[400px]">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                            <RefreshCw className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                            <DialogTitle>Update Status</DialogTitle>
                            <DialogDescription>{lead.id} — {lead.name}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="py-2 space-y-4">
                    <p className="text-sm text-muted-foreground">Current status: <StatusBadge status={lead.status} /></p>
                    <div className="space-y-1.5">
                        <Label>New Status</Label>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                            <SelectContent>
                                {['Contacted', 'Converted', 'Pending', 'Rejected'].map(s => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700 text-white" disabled={saving || !status}>
                        {saving ? <><RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> Updating...</> : 'Update Status'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ─── Generic Delete Modal ────────────────────────────────────────────────────
export const DeleteModal = ({ open, onClose, title, description, onConfirm }) => {
    const [deleting, setDeleting] = useState(false);
    const handleConfirm = async () => {
        setDeleting(true);
        try {
            await onConfirm?.();
            toast.success(title ? `${title} deleted successfully.` : 'Record deleted successfully.');
            onClose();
        } catch (err) {
            toast.error(err?.message || 'Failed to delete record');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[420px]">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                            <AlertTriangle className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                            <DialogTitle>Confirm Delete</DialogTitle>
                            <DialogDescription>This action cannot be undone.</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="py-3 px-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <p className="text-sm text-red-700 dark:text-red-400 font-medium">
                        {description || 'Are you sure you want to delete this record? All associated data will be permanently removed.'}
                    </p>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={deleting}>Cancel</Button>
                    <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleConfirm} disabled={deleting}>
                        {deleting ? <><RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> Deleting...</> : <><Trash2 className="h-4 w-4 mr-1.5" /> Delete</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// FOLLOW-UP MODALS  (used by Agent & Employee panels)
// ═══════════════════════════════════════════════════════════════════════════

export const ScheduleFollowUpModal = ({ lead, open, onClose, token, onSaved }) => {
    const [form, setForm] = useState({
        type: 'Call',
        date: '',
        time: '',
        notes: '',
        priority: 'Medium',
    });
    const [saving, setSaving] = useState(false);

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.date || !form.time) {
            toast.error('Please select a date and time.');
            return;
        }
        setSaving(true);
        try {
            if (token && lead?.id) {
                await createLeadFollowUp(token, lead.id, {
                    follow_up_type: form.type,
                    date: form.date,
                    time: form.time,
                    priority: form.priority,
                    notes: form.notes || null,
                });
            }
            toast.success(`Follow-up scheduled for ${lead?.name || 'this lead'} on ${form.date} at ${form.time}`);
            onSaved?.();
            onClose();
        } catch (err) {
            toast.error(err?.message || 'Failed to schedule follow-up');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[460px]">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30">
                            <CalendarDays className="h-5 w-5 text-teal-600" />
                        </div>
                        <div>
                            <DialogTitle>Schedule Follow-Up</DialogTitle>
                            <DialogDescription>{lead?.name ? `${lead.name} — ${lead.id || lead.leadId}` : 'Set a follow-up reminder'}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <form onSubmit={handleSave} className="space-y-4">
                    <div className="space-y-1.5">
                        <Label>Follow-Up Type</Label>
                        <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {['Call', 'Meeting', 'Site Visit', 'WhatsApp', 'Email'].map(t => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>Date</Label>
                            <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} required />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Time</Label>
                            <Input type="time" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} required />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Priority</Label>
                        <Select value={form.priority} onValueChange={v => setForm(p => ({ ...p, priority: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="High">High</SelectItem>
                                <SelectItem value="Medium">Medium</SelectItem>
                                <SelectItem value="Low">Low</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Notes / Remarks</Label>
                        <Textarea
                            placeholder="Add notes about what to discuss..."
                            value={form.notes}
                            onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                            rows={3}
                            className="resize-none"
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white" disabled={saving}>
                            {saving ? <><RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> Scheduling...</> : <><CalendarDays className="h-4 w-4 mr-1.5" /> Schedule</>}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export const MarkDoneModal = ({ followUp, open, onClose }) => {
    const [outcome, setOutcome] = useState('');
    const [notes, setNotes] = useState('');
    const [nextAction, setNextAction] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSave = async (e) => {
        e.preventDefault();
        if (!outcome) { toast.error('Please select an outcome.'); return; }
        setSaving(true);
        await new Promise(r => setTimeout(r, 700));
        setSaving(false);
        toast.success(`Follow-up for ${followUp?.leadName || 'this lead'} marked as done!`);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[460px]">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                            <DialogTitle>Mark Follow-Up Done</DialogTitle>
                            <DialogDescription>{followUp?.leadName} — {followUp?.leadId}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <form onSubmit={handleSave} className="space-y-4">
                    {/* Current follow-up info */}
                    <div className="p-3 rounded-xl bg-muted/50 space-y-1.5 text-sm">
                        <div className="flex items-center gap-2">
                            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-muted-foreground">Scheduled:</span>
                            <span className="font-medium">{followUp?.scheduledDate} at {followUp?.scheduledTime}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-muted-foreground">Type:</span>
                            <span className="font-medium">{followUp?.type}</span>
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label>Outcome *</Label>
                        <Select value={outcome} onValueChange={setOutcome}>
                            <SelectTrigger><SelectValue placeholder="What was the outcome?" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Interested">Interested — Will Proceed</SelectItem>
                                <SelectItem value="Callback">Requested Callback</SelectItem>
                                <SelectItem value="Not Interested">Not Interested</SelectItem>
                                <SelectItem value="No Answer">No Answer</SelectItem>
                                <SelectItem value="Converted">Converted to File</SelectItem>
                                <SelectItem value="Rejected">Customer Rejected</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-1.5">
                        <Label>Call Notes</Label>
                        <Textarea
                            placeholder="What was discussed? Key points..."
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            rows={2}
                            className="resize-none"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label>Next Action (optional)</Label>
                        <Select value={nextAction} onValueChange={setNextAction}>
                            <SelectTrigger><SelectValue placeholder="Schedule next step?" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="schedule_followup">Schedule Another Follow-Up</SelectItem>
                                <SelectItem value="send_docs">Send Document Checklist</SelectItem>
                                <SelectItem value="file_login">Proceed to File Login</SelectItem>
                                <SelectItem value="close">Close Lead</SelectItem>
                                <SelectItem value="none">No Further Action</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={saving}>
                            {saving ? <><RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> Saving...</> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark Done</>}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export const RescheduleModal = ({ followUp, open, onClose }) => {
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSave = async (e) => {
        e.preventDefault();
        if (!date || !time) { toast.error('Please select a new date and time.'); return; }
        setSaving(true);
        await new Promise(r => setTimeout(r, 600));
        setSaving(false);
        toast.success(`Follow-up rescheduled to ${date} at ${time}`);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[420px]">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                            <Clock className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                            <DialogTitle>Reschedule Follow-Up</DialogTitle>
                            <DialogDescription>{followUp?.leadName}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <form onSubmit={handleSave} className="space-y-4">
                    <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                        <p className="text-sm text-amber-700 dark:text-amber-400 font-medium">
                            Currently: {followUp?.scheduledDate} at {followUp?.scheduledTime}
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>New Date</Label>
                            <Input type="date" value={date} onChange={e => setDate(e.target.value)} required />
                        </div>
                        <div className="space-y-1.5">
                            <Label>New Time</Label>
                            <Input type="time" value={time} onChange={e => setTime(e.target.value)} required />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Reason for Rescheduling</Label>
                        <Textarea
                            placeholder="Why is this being rescheduled?"
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            rows={2}
                            className="resize-none"
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white" disabled={saving}>
                            {saving ? <><RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> Rescheduling...</> : <><Clock className="h-4 w-4 mr-1.5" /> Reschedule</>}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// AGENT MODALS
// ═══════════════════════════════════════════════════════════════════════════

export const ViewAgentModal = ({ agent, open, onClose }) => {
    const { user } = useAuth();
    if (!agent) return null;
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[520px]">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                            <Eye className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <DialogTitle>Agent Profile</DialogTitle>
                            <DialogDescription>{agent.id}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 mb-2">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-lg">
                        {agent.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                        <p className="font-bold text-base">{agent.name}</p>
                        <StatusBadge status={agent.status} />
                    </div>
                </div>

                <div className="space-y-0 rounded-xl border overflow-hidden">
                    <InfoRow icon={Phone} label="Phone" value={agent.phone} />
                    <InfoRow icon={Mail} label="Email" value={agent.email} />
                    <InfoRow icon={Calendar} label="Joined" value={agent.joinDate} />
                    {(user?.role === 'super_admin' || user?.role === 'wl_admin') && (
                        <InfoRow icon={UserPlus} label="Created By" value={agent.createdBy} valueClass="text-blue-600" />
                    )}
                    <InfoRow icon={User} label="Total Leads" value={String(agent.totalLeads)} />
                    <InfoRow icon={CheckCircle2} label="Converted" value={String(agent.convertedLeads)} valueClass="text-emerald-600" />
                    <InfoRow icon={DollarSign} label="Commission Earned" value={fmt(agent.commissionEarned)} valueClass="text-emerald-600" />
                    <InfoRow icon={DollarSign} label="Pending Commission" value={fmt(agent.pendingCommission)} valueClass="text-amber-600" />
                    <InfoRow icon={Tag} label="Rating" value={`⭐ ${agent.rating}`} />
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const EditAgentModal = ({ agent, open, onClose, token, onSaved }) => {
    const [form, setForm] = useState(agent || {});
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { user } = useAuth();
    const isWlAdmin = user?.role === 'wl_admin';

    useEffect(() => {
        if (agent) setForm({ ...agent, password: '', target: agent.target ?? 0 });
    }, [agent]);

    if (!agent) return null;

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            let resp = null;
            if (token) {
                const payload = {
                    full_name: (form.name || '').trim(),
                    phone: (form.phone || '').trim() || null,
                    target: Math.max(0, Number(form.target) || 0),
                    status: (form.status || 'Active').toLowerCase() === 'active' ? 'active' : 'inactive',
                };
                // WL Admin gets limited edit scope: profile basics only.
                if (!isWlAdmin) {
                    payload.email = (form.email || '').trim();
                }
                if (!isWlAdmin && form.password && form.password.length >= 6) {
                    payload.password = form.password;
                }
                resp = await updateAgent(token, agent.id, payload);
            }
            toast.success('Agent updated successfully!');
            onSaved?.(resp?.data || null);
            onClose();
        } catch (err) {
            toast.error(err?.message || 'Failed to update agent');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                            <Edit className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                            <DialogTitle>Edit Agent</DialogTitle>
                            <DialogDescription>{agent.id} — {agent.name}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <form onSubmit={handleSave} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>Full Name</Label>
                            <Input value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Phone</Label>
                            <Input value={form.phone || ''} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} required />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Email</Label>
                        <Input
                            type="email"
                            value={form.email || ''}
                            onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                            required
                            disabled={isWlAdmin}
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Monthly Target</Label>
                        <Input
                            type="number"
                            min="0"
                            step="1"
                            value={form.target ?? 0}
                            onChange={e => setForm(p => ({ ...p, target: e.target.value }))}
                            required
                        />
                    </div>
                    {!isWlAdmin && (
                        <div className="space-y-1.5">
                            <Label>New Password (leave blank to keep current)</Label>
                            <div className="relative">
                                <Input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Min 8 characters"
                                    value={form.password || ''}
                                    onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                                    minLength={8}
                                    className="pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="space-y-1.5">
                        <Label>Status</Label>
                        <Select value={form.status || 'Active'} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Active">Active</SelectItem>
                                <SelectItem value="Inactive">Inactive</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white" disabled={saving}>
                            {saving ? <><RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> Saving...</> : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

// UpdateAgentStatusModal has been deprecated; status is handled inside EditAgentModal.

// ═══════════════════════════════════════════════════════════════════════════
// EMPLOYEE MODALS
// ═══════════════════════════════════════════════════════════════════════════

export const ViewEmployeeModal = ({ employee, open, onClose }) => {
    if (!employee) return null;
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[520px]">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                            <Eye className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <DialogTitle>Employee Profile</DialogTitle>
                            <DialogDescription>{employee.id}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 mb-2">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                        {employee.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                        <p className="font-bold text-base">{employee.name}</p>
                        <StatusBadge status={employee.status} />
                    </div>
                </div>

                <div className="space-y-0 rounded-xl border overflow-hidden">
                    <InfoRow icon={Phone} label="Phone" value={employee.phone} />
                    <InfoRow icon={Mail} label="Email" value={employee.email} />
                    <InfoRow icon={User} label="Role" value={employee.role} />
                    <InfoRow icon={FileText} label="Department" value={employee.department} />
                    <InfoRow icon={Calendar} label="Joined" value={employee.joinDate} />
                    <InfoRow icon={UsersRound} label="Created Agents" value={String(employee.createdAgents ?? 0)} />
                    <InfoRow icon={Tag} label="Assigned Leads" value={String(employee.assignedLeads)} />
                    <InfoRow icon={CheckCircle2} label="Processed Files" value={String(employee.processedFiles)} valueClass="text-emerald-600" />
                    <InfoRow icon={RefreshCw} label="Performance" value={`${employee.performance}%`} valueClass={employee.performance >= 80 ? 'text-emerald-600' : 'text-amber-600'} />
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const statusDisplayToApi = (s) => {
    if (s === 'Inactive') return 'inactive';
    if (s === 'On Leave') return 'suspended';
    return 'active';
};

export const EditEmployeeModal = ({ employee, open, onClose, token, roles = [], departments = [] }) => {
    const [form, setForm] = useState(employee || {});
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    useEffect(() => {
        if (employee) setForm({ ...employee });
    }, [employee]);
    if (!employee) return null;

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (token) {
                const payload = {
                    full_name: (form.name || '').trim(),
                    email: (form.email || '').trim(),
                    phone: (form.phone || '').trim() || null,
                    status: statusDisplayToApi(form.status),
                    department_id: form.department_id || null,
                    employee_role_id: form.employee_role_id || null,
                };
                if (form.password && form.password.length >= 6) payload.password = form.password;
                await updateEmployee(token, employee.id, payload);
            }
            toast.success('Employee updated successfully!');
            onClose();
        } catch (err) {
            toast.error(err?.message || 'Failed to update employee');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                            <Edit className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                            <DialogTitle>Edit Employee</DialogTitle>
                            <DialogDescription>{employee.id} — {employee.name}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <form onSubmit={handleSave} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>Full Name</Label>
                            <Input value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Phone</Label>
                            <Input value={form.phone || ''} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Email</Label>
                        <Input type="email" value={form.email || ''} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required />
                    </div>
                    <div className="space-y-1.5">
                        <Label>New Password (leave blank to keep current)</Label>
                        <div className="relative">
                            <Input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Min 8 characters"
                                value={form.password || ''}
                                onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                                minLength={8}
                                className="pr-10"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>Role</Label>
                            <Select value={form.employee_role_id || '_none'} onValueChange={v => setForm(p => ({ ...p, employee_role_id: v === '_none' ? '' : v }))}>
                                <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="_none">— None —</SelectItem>
                                    {roles.map(r => (
                                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Department</Label>
                            <Select value={form.department_id || '_none'} onValueChange={v => setForm(p => ({ ...p, department_id: v === '_none' ? '' : v }))}>
                                <SelectTrigger><SelectValue placeholder="Select dept" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="_none">— None —</SelectItem>
                                    {departments.map(d => (
                                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Status</Label>
                        <Select value={form.status || 'Active'} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Active">Active</SelectItem>
                                <SelectItem value="On Leave">On Leave</SelectItem>
                                <SelectItem value="Inactive">Inactive</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white" disabled={saving}>
                            {saving ? <><RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> Saving...</> : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// LOAN / FILE LOGIN MODALS
// ═══════════════════════════════════════════════════════════════════════════

const docLabelMap = {
    panCard: 'PAN Card',
    aadhaarCard: 'Aadhaar Card',
    salarySlips: 'Salary Slips',
    bankStatement: 'Bank Statement',
    propertyDocs: 'Property Documents',
    photograph: 'Photograph',
    gstCert: 'GST Certificate',
    itr: 'ITR',
    bizProof: 'Business Registration Proof',
    dealerQuote: 'Dealer Quotation',
    studentId: 'Student ID / Marksheet',
    admission: 'Admission Letter',
    feeStructure: 'Fee Structure',
    incomeProof: 'Income Proof',
};

export const ViewLoanModal = ({ loan, open, onClose }) => {
    if (!loan) return null;
    const details = loan.product_details || loan.productDetails || {};
    const documents = Array.isArray(details.documents) ? details.documents : [];
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[540px]">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                            <Eye className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <DialogTitle>Loan File Details</DialogTitle>
                            <DialogDescription>{loan.id}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 mb-2">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center text-white font-bold text-lg">
                        {loan.applicantName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                        <p className="font-bold text-base">{loan.applicantName}</p>
                        <StatusBadge status={loan.status} />
                    </div>
                </div>

                <div className="space-y-0 rounded-xl border overflow-hidden">
                    <InfoRow icon={Tag} label="Loan Type" value={loan.loanType} />
                    <InfoRow icon={FileText} label="Bank / NBFC" value={loan.bank} />
                    <InfoRow icon={DollarSign} label="Loan Amount" value={fmt(loan.amount)} valueClass="text-emerald-600" />
                    <InfoRow icon={Tag} label="Interest Rate" value={loan.interestRate > 0 ? `${loan.interestRate}%` : '—'} />
                    <InfoRow icon={Calendar} label="Tenure" value={loan.tenure > 0 ? `${loan.tenure} months` : '—'} />
                    <InfoRow icon={DollarSign} label="EMI" value={loan.emi > 0 ? fmt(loan.emi) : '—'} />
                    <InfoRow icon={FileText} label="Stage" value={loan.stage} />
                    <InfoRow icon={Calendar} label="Applied Date" value={loan.appliedDate || loan.created_at || '—'} />
                </div>

                {Object.keys(details).length > 0 && (
                    <div className="mt-4 space-y-3">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Application Details</p>
                        <div className="rounded-xl border overflow-hidden text-sm">
                            <div className="px-4 py-2 bg-muted font-medium">Core Details</div>
                            <div className="p-4 space-y-1">
                                {details.basic && (
                                    <p><span className="font-medium">Customer:</span> {details.basic.fullName || loan.applicantName}</p>
                                )}
                                {details.personal && (
                                    <p><span className="font-medium">Customer:</span> {details.personal.fullName || loan.applicantName}</p>
                                )}
                                {details.student && (
                                    <p><span className="font-medium">Student:</span> {details.student.studentName || loan.applicantName}</p>
                                )}
                                {details.income && details.income.monthlyIncome && (
                                    <p><span className="font-medium">Monthly Income:</span> ₹{Number(details.income.monthlyIncome).toLocaleString('en-IN')}</p>
                                )}
                                {details.property && details.property.propertyLocation && (
                                    <p><span className="font-medium">Property Location:</span> {details.property.propertyLocation}</p>
                                )}
                                {details.business && details.business.businessName && (
                                    <p><span className="font-medium">Business:</span> {details.business.businessName}</p>
                                )}
                                {details.vehicle && details.vehicle.carModel && (
                                    <p><span className="font-medium">Car Model:</span> {details.vehicle.carModel}</p>
                                )}
                                {details.coApplicant && details.coApplicant.coName && (
                                    <p><span className="font-medium">Co-Applicant:</span> {details.coApplicant.coName}</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {documents.length > 0 && (
                    <div className="mt-4 space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Uploaded Documents</p>
                        <div className="rounded-xl border overflow-hidden">
                            <table className="w-full text-sm">
                                <tbody>
                                    {documents.map(function (doc) {
                                        var label = docLabelMap[doc.key] || doc.key;
                                        return (
                                            <tr key={doc.key} className="border-b last:border-0">
                                                <td className="px-4 py-2.5 font-medium text-slate-600 dark:text-slate-300 w-1/2">{label}</td>
                                                <td className="px-4 py-2.5 text-slate-900 dark:text-slate-100">
                                                    {doc.file_name || 'Uploaded'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const UpdateLoanStatusModal = ({ loan, open, onClose }) => {
    const [status, setStatus] = useState(loan?.status || '');
    const [remarks, setRemarks] = useState('');
    const [saving, setSaving] = useState(false);
    if (!loan) return null;

    const handleSave = async () => {
        setSaving(true);
        await new Promise(r => setTimeout(r, 700));
        setSaving(false);
        toast.success(`Loan status updated to "${status}"`);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[420px]">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                            <RefreshCw className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                            <DialogTitle>Update Loan Status</DialogTitle>
                            <DialogDescription>{loan.id} — {loan.applicantName}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <div className="py-2 space-y-4">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                        <span className="text-sm text-muted-foreground">Current Status</span>
                        <StatusBadge status={loan.status} />
                    </div>
                    <div className="space-y-1.5">
                        <Label>New Status</Label>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                            <SelectContent>
                                {['Under Processing', 'Approved', 'Disbursed', 'Rejected', 'Hold'].map(s => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Remarks (optional)</Label>
                        <Input placeholder="Add notes about this status change..." value={remarks} onChange={e => setRemarks(e.target.value)} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700 text-white" disabled={saving || !status}>
                        {saving ? <><RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> Updating...</> : 'Update Status'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
